import { useState } from "react";
import styled from "styled-components";

import Dialog from "src/components/core/Dialog";
import Button from "src/components/core/Button";
import { Stack, IconButton, FormHelperText } from "@mui/material";
import TextField from "src/components/core/TextField";
import CloseIcon from "@mui/icons-material/Close";
import CameraIcon from "src/components/icon/Camera";

import { DialogTitle, DialogContent, DialogActions } from "@mui/material";

import font from "src/styles/font";
import { useDialog } from "src/context/DialogContext";
import { useSnackbar } from "notistack";
import useBanner from "src/hooks/useBanner";

const S = {};

S.PreviewImageUploadBox = styled.div`
  position: relative;
  padding-top: calc(100% / (1250 / 280));
  background-color: #f3f1f2;
  border-radius: 24px;
  /* grid-column: span 7; */
`;

S.UploadInnerBox = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

S.UploadInput = styled.label`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
`;

S.UploadLabel = styled.span`
  font-size: ${font.size.md};
  font-weight: bold;
  color: #6c6c70;
  margin-top: 16px;

  &::after {
    content: "*";
    font-weight: 600;
    color: #e04132;
  }
`;

S.UploadHelperText = styled.span`
  display: flex;
  justify-content: center;
  font-size: ${font.size.sm};
  color: #6c6c70;
  margin-top: 8px;
`;

S.UploadedPreviewImage = styled.img`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  border-radius: 15px;
  object-fit: cover;
`;

const BannerHomeEditDialog = ({ onClose, onSuccess, initialValue }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { openDialog, closeDialog } = useDialog();

  const { updateBanner } = useBanner({
    bannerKey: "shop_this_moods",
    resource: "homepage",
    options: {
      autoFetch: false,
    },
  });

  const [formData, setFormData] = useState(initialValue);

  const handleSubmit = async () => {
    try {
      const payload = {
        ...formData,
      };

      const data = new FormData();
      for (const field in payload) {
        data.append(field, payload[field]);
      }

      await updateBanner(data);

      enqueueSnackbar(`อัปโหลดภาพสำเร็จ`, {
        variant: "success",
      });

      onSuccess();
    } catch (err) {
      enqueueSnackbar(err.message, {
        variant: "error",
      });
      closeDialog();
      console.error(err);
    }
  };

  return (
    <Dialog
      onClose={onClose}
      aria-labelledby="customized-dialog-title"
      open={true}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: 1024,
            maxWidth: "100%",
          },
        },
        "& .MuiDialogContent-root": {
          pt: 1,
          pb: 4,
        },
      }}
    >
      <DialogTitle sx={{ p: 3 }}>
        {initialValue ? "แก้ไข" : "เพิ่ม"}ภาพแบนเนอร์
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <TextField
          label="ลิงค์"
          placeholder="e.g. https://www.xsurface.com/xclusive-deal/product-list/663b389aa860807025656094?product_types=&page=1&keyOrder=asc"
          variant="outlined"
          required
          value={formData?.link}
          onChange={(e) => {
            setFormData({ ...formData, link: e.target.value });
          }}
          fullWidth
          error={!formData?.link}
          sx={{
            mb: 2,
          }}
        />
        <S.PreviewImageUploadBox>
          {!formData?.image ? (
            <S.UploadInnerBox>
              <Stack flexDirection={"row"}>
                <input
                  id="previewImgFieldHandle"
                  type="file"
                  accept=".jpg,.jpeg,.png"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    e.preventDefault();
                    setFormData({ ...formData, image: e.target.files[0] });
                  }}
                  onClick={(e) => (e.currentTarget.value = null)}
                />
                <S.UploadInput htmlFor="previewImgFieldHandle">
                  <CameraIcon size={60} color="#BCBCC0" />
                  <S.UploadLabel>อัปโหลดแบนเนอร์</S.UploadLabel>
                  <S.UploadHelperText>
                    รูปภาพขนาด 1250x280 px ขนาดไฟล์สูงสุดไม่เกิน 4 MB
                  </S.UploadHelperText>
                </S.UploadInput>
              </Stack>
            </S.UploadInnerBox>
          ) : (
            <>
              <S.UploadedPreviewImage
                src={
                  typeof formData?.image === "string"
                    ? formData?.image
                    : URL.createObjectURL(formData?.image)
                }
                alt=""
              />
              <Stack
                sx={{
                  position: "absolute",
                  top: 6,
                  right: 6,
                }}
                flexDirection={"row"}
                columnGap={1}
              >
                <IconButton
                  sx={{
                    padding: "4px",
                    backgroundColor: "#BCBCC0bd",
                  }}
                  onClick={() => {
                    setFormData({ ...formData, image: null });
                  }}
                >
                  <CloseIcon sx={{ color: "#ffffff" }} />
                </IconButton>
              </Stack>
            </>
          )}
        </S.PreviewImageUploadBox>
        {!formData?.image && (
          <FormHelperText error={!formData?.image}>
            กรุณาเลือกรูปภาพแบนเนอร์
          </FormHelperText>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          justifyContent: "center",
        }}
      >
        <Button
          variant="outlined"
          size="large"
          onClick={onClose}
          sx={{
            minWidth: 160,
          }}
        >
          ยกเลิก
        </Button>
        <Button
          variant="contained"
          size="large"
          onClick={() => {
            openDialog({
              type: "confirm",
              title: `คุณต้องการอัปโหลดภาพใช่หรือไม่?`,
              onConfirm: async () => {
                await handleSubmit();
              },
              maxWidth: 342,
            });
          }}
          sx={{
            minWidth: 160,
          }}
          // autoFocus // enable auto focus to increase accessibility
          disabled={!formData?.image || !formData?.link}
        >
          {initialValue ? "ยืนยัน" : "อัพโหลด"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BannerHomeEditDialog;
